import React, { useEffect, useState } from 'react'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogActions from '@material-ui/core/DialogActions'
import CloseIcon from '@material-ui/icons/Close'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import {
  Button,
  TextField,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@material-ui/core'
import { REACT_APP_SANDBOX, REACT_APP_VOYAGER } from '@config/env'
import { generatePassword } from '@lib/utils/password'
import useDashboardStyles from '@components/GlobalStyles'

interface ICreateInstanceModalProps {
  open: boolean
  addInstance: (
    instanceId: string,
    username: string,
    password: string,
    sandbox?: boolean,
    voyager?: boolean
  ) => void
  handleClose: () => void
}

export default function CreateInstanceModalView({
  open,
  addInstance: addInstance,
  handleClose,
}: ICreateInstanceModalProps) {
  const theme = useTheme()
  const classes = useDashboardStyles(theme)

  // create state variables for each input
  const [instanceName, setInstanceName] = useState('')
  const [adminUsername, setAdminUsername] = useState('')
  const [password, setPassword] = useState('')
  const [sandbox, setSandbox] = useState(false)
  const [voyager, setVoyager] = useState(false)

  useEffect(() => {
    setInstanceName('')
    setAdminUsername('')
    setPassword(generatePassword())
    setSandbox(false)
    setVoyager(false)
  }, [open])

  const handleInstanceNameChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setInstanceName(event.target.value)
    const adminName = `admin-${event.target.value}`
    setAdminUsername(adminName.toLowerCase())
  }

  const handleSandboxToggle = () => {
    if (REACT_APP_SANDBOX) {
      setSandbox(!sandbox)
    }
  }
  const handleVoyagerToggle = () => {
    if (REACT_APP_VOYAGER) {
      setVoyager(!voyager)
    }
  }
  const handleSubmit = (event: React.MouseEvent) => {
    event.preventDefault()
    addInstance(instanceName, adminUsername, password, sandbox, voyager)
  }

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth
      >
        <MuiDialogTitle>
          <Typography style={{ color: theme.palette.text.secondary }}>
            Add New Dashboard Instance
          </Typography>
          {handleClose ? (
            <IconButton
              className={classes.closeButton}
              aria-label="close"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </MuiDialogTitle>
        <hr
          color={theme.palette.grey[500]}
          style={{ width: '100%', borderTop: '3pt', margin: 0 }}
        ></hr>
        <Grid container justify="space-around" alignItems={'center'}>
          <Grid item xs={9} style={{ marginTop: 10 }}>
            <TextField
              label="Dashboard Instance ID"
              variant="outlined"
              required
              value={instanceName}
              className={classes.siteTextField}
              onChange={(e) => handleInstanceNameChange(e)}
            />
          </Grid>
          <Grid item xs={9} style={{ marginTop: 10 }}>
            <TextField
              label="Instance Admin Username"
              variant="outlined"
              required
              aria-readonly
              value={adminUsername}
              className={classes.siteTextField}
              title="Auto generated.  You cannot change this value manually."
            />
          </Grid>
          <Grid item xs={9} style={{ marginTop: 10 }}>
            <TextField
              label="Password"
              variant="outlined"
              required
              aria-readonly
              value={password}
              className={classes.siteTextField}
              title="WARNING: Lost passwords will need to be reset by a Dexcom Engineer manually!"
            />
          </Grid>
          {REACT_APP_SANDBOX && (
            <Grid item xs={9} style={{ marginTop: 10 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="sandbox-toggle"
                    onChange={handleSandboxToggle}
                    checked={sandbox}
                    color="primary"
                  />
                }
                label="Initialize Sandbox Participants"
              />
            </Grid>
          )}
          {REACT_APP_VOYAGER && (
            <Grid item xs={9} style={{ marginTop: 10 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="voyager-toggle"
                    onChange={handleVoyagerToggle}
                    checked={voyager}
                    color="primary"
                  />
                }
                label="Voyager Features"
              />
            </Grid>
          )}
        </Grid>

        <MuiDialogActions>
          <Button onClick={handleClose} color="default" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Create Instance
          </Button>
        </MuiDialogActions>
      </Dialog>
    </React.Fragment>
  )
}
